import React from "react"
import {useTranslation} from "react-i18next"

// HOCs
import withRecordData, {WithRecordDataProps} from "../../../hoc/withRecordData"

// Components
import Icon from "../../../../../components/modern/Icon"

// Utils
import {patientAge, patientSex} from "../patientFields"
import {formatDuration} from "../ModernEcgGrid/window"

// Styles
import useStyles from "./styles"

const EM_DASH = "—";

type ModernPatientCardOwnProps = {
    /** Opens <ModernClinicalDialog/>, which ModernRecordPage owns so the "O" shortcut can share it. */
    onOpenClinical(): void
    durationSec: number | null
}

type ModernPatientCardProps = ModernPatientCardOwnProps & WithRecordDataProps

function display(value: string | number | null | undefined): string {
    if (value === null || typeof value === "undefined" || value === "") {
        return EM_DASH
    }

    return String(value)
}

/**
 * Patient / acquisition identity card.
 *
 * Row order and labels follow the design prototype: ID / Name, Last name / Age,
 * Gender / Duration, Device. Three of those labels read differently in Classic
 * ("Surname", "Sex", "Device ID"), so they come from Modern-only i18n keys —
 * `records.surname`, `records.sex` and `records.deviceId` are shared with
 * <GeneralInfoSection/>, and rewording them here would silently relabel the
 * Classic skin as well.
 *
 * The underlying fields are unchanged: production `patientInfo` uses `surName`
 * (not `lastName`) and `metaData` uses `sex` (MALE / FEMALE, not `gender`).
 *
 * `selectRecordMetaData` and `selectRecordPatientInfo` return null even though
 * `WithRecordDataProps` types them as non-null, so everything below reads
 * through optional access rather than repeating Classic's unguarded destructure.
 *
 * @param props
 */
function ModernPatientCard(props: ModernPatientCardProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {record, metaData, patientInfo, onOpenClinical, durationSec} = props;

    if (!record) {
        return null
    }

    const sex = patientSex(metaData, patientInfo);
    const age = patientAge(metaData, patientInfo);

    function renderRow(label: string, value: string) {
        return (
            <div className={styles.row}>
                <span className={styles.row_label}>{label}</span>
                <span className={styles.row_value} title={value !== EM_DASH ? value : undefined}>{value}</span>
            </div>
        )
    }

    return (
        <section className={styles.sect}>
            <div className={styles.sect_head}>
                <span className={styles.microlabel}>{t("modern.patient")}</span>
                {record.recordType ? <span className={styles.pill}>{record.recordType}</span> : null}
            </div>

            <div className={styles.grid}>
                {renderRow("ID", display(patientInfo && patientInfo.id))}
                {renderRow(t("records.name"), display(patientInfo && patientInfo.firstName))}
                {renderRow(t("modern.last_name"), display(patientInfo && patientInfo.surName))}
                {renderRow(t("records.age"), display(age))}
                {renderRow(t("modern.gender"), sex ? t(`records.${sex}`, {defaultValue: sex}) as string : EM_DASH)}
                {/*
                  */}
                {renderRow(t("records.duration"), durationSec === null ? EM_DASH : formatDuration(durationSec))}
                {renderRow(t("modern.device"), display(metaData && metaData.deviceId))}
            </div>

            <button type="button" className={styles.clinical_link} onClick={onOpenClinical} aria-haspopup="dialog">
                <Icon name="info"/>
                {t("modern.clinical_info")}
            </button>
        </section>
    )
}

export {ModernPatientCard}

export default withRecordData(ModernPatientCard) as React.ComponentType<ModernPatientCardOwnProps>
